import React, { useRef, useEffect } from 'react';
import './CalculusOfMeaning.css';

/* -- Neural Particle Network -- */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 45;
    const CONNECT_DIST = 130;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

/* -- Animated Neural Pulse Divider -- */
function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* -- Main Page -- */
function WillingnessToPay() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted loop playsInline className="calculus-hero-video">
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Transaction Theory &middot; Trust Economics &middot; Human Systems</span>
          <h1>The Willingness to Pay</h1>
          <p className="calculus-hero-sub">It has nothing to do with the price. Nothing to do with the ability. Everything to do with what you understand, what you value, and what you're afraid to lose.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* Opening */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              Every exchange between every node in every system, from a grocery store to a
              nation going to war, runs on a single function: the willingness to pay.
            </p>
            <p>
              Not the ability. Not the price. The <strong>willingness</strong>.
            </p>
            <p>
              A woman with a six-figure bank account will return a $3 item because the register
              rang up fifty cents more than the ad promised. A mother making $16 an hour will drive
              through a whiteout to hear a doctor say "I don't know" and pay $150 for the privilege.
            </p>
            <p>
              Same species. Same economy. Completely different willingness. Because willingness to
              pay is not an economic function. It is a <em>human</em> function. And it is governed
              by six variables that have nothing to do with money.
            </p>
          </section>

          <Pulse />

          {/* The Six Variables */}
          <section className="calc-section reveal">
            <h2>The Six Variables</h2>

            <div className="calc-attempts">
              <div className="calc-attempt">
                <h4>1. Understanding</h4>
                <p>Do you comprehend what you are being offered? A person who does not understand what the NCA is has zero willingness to pay for it. Not because it lacks value. Because the value is invisible to them. Understanding is the gate. Everything else follows.</p>
              </div>
              <div className="calc-attempt">
                <h4>2. Personal Value</h4>
                <p>What does this mean to YOU? Not the market. Not the average consumer. You. The same object has different value to different nodes. A fish oil supplement is worth nothing to most people. To a mother whose daughter is in pain, it is worth a six-hour drive through a blizzard.</p>
              </div>
              <div className="calc-attempt">
                <h4>3. Desire</h4>
                <p>How badly do you want it? Desire is not value. You can value something abstractly and not want it. Desire is the active pull. The urgency. The thing that makes you drive through whiteout conditions instead of waiting for a phone call that never comes.</p>
              </div>
              <div className="calc-attempt">
                <h4>4. Availability</h4>
                <p>Can you get it somewhere else? Scarcity amplifies willingness. When the doctor refuses to give results over the phone, they have manufactured artificial scarcity. You cannot get the answer anywhere else. Only here. Only in person. Only at their price.</p>
              </div>
              <div className="calc-attempt">
                <h4>5. Market Evaluation</h4>
                <p>Is this price fair relative to what you know? The market sets expectations. The advertised price. The going rate. The comparable. When the actual cost deviates from the evaluated cost, willingness shifts. $150 for a diagnosis is reasonable. $150 for "I don't know" is zero value at any price.</p>
              </div>
              <div className="calc-attempt">
                <h4>6. Promise</h4>
                <p>What were you told before the transaction? Promise is the pre-commitment that sets the expected price. It is the advertised sale. The prior authorization. The verbal confirmation. When the promise is honored, trust deepens. When the promise is broken, willingness to pay does not decrease. It collapses to zero. Instantly.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* The Grocery Store */}
          <section className="calc-section reveal">
            <h2>The Grocery Store</h2>
            <p>
              A woman with a six-figure account balance walks into a grocery store. She has
              seen the ad. She knows the price. She has made her decision before she enters
              the building.
            </p>
            <p>
              The register rings up a different number.
            </p>
            <p>
              It is not about the money. She has more money than most people will see in a
              year. It is about the <strong>promise</strong>. The advertised price is a contract.
              The store made a commitment. The register broke it.
            </p>
            <p>
              She will argue. She will demand the advertised price. She will return the item.
              She will sever her relationship with the entire store. Over fifty cents. Because
              willingness to pay collapsed the moment trust was violated.
            </p>

            <div className="calc-thesis-box">
              <p>
                Economic status does not determine willingness to pay. It determines
                <strong> options</strong>. The wealthy can afford to walk away. So the trust
                variable weighs heavier. They have alternatives. The store needs them more
                than they need the store. A broken promise costs the store a customer worth
                thousands in lifetime revenue. Over fifty cents.
              </p>
            </div>
          </section>

          <Pulse />

          {/* The Whiteout */}
          <section className="calc-section reveal">
            <h2>The Whiteout</h2>
            <p>
              A mother takes her daughter to a specialist. An hour and a half away. The child
              is in excruciating pain. Blood work is drawn. Results come back.
            </p>
            <p>
              The office refuses to discuss results over the phone. They require an in-person
              visit. Outside, the roads are disappearing under snow.
            </p>
            <p>
              The mother drives. Through whiteout conditions. Because her daughter's health
              is the highest-value variable in her entire system. Desire is maximum.
              Availability has been artificially restricted. The only place the answer exists
              is in that office. At their price. On their terms.
            </p>
            <p>
              She arrives. She pays. The doctor says: <em>"I don't know what it means."</em>
            </p>
            <p>
              $150. For nothing. The product was zero. The value was zero. But the transaction
              was already complete. They had her in the chair before she knew the product was empty.
            </p>

            <div className="calc-thesis-box">
              <p>
                They weaponized her willingness to pay. They understood that a mother's fear
                for her child creates the highest willingness to pay in human existence. So they
                manufactured scarcity (no phone results), exploited desire (her child's health),
                and extracted payment for a product that did not exist.
              </p>
            </div>

            <p>
              She drove another three and a half hours that same day to another specialist.
              Because her willingness to pay did not collapse. It <em>transferred</em>. The
              desire was unchanged. The trust shifted to a new provider. And the answer, after
              eight hours of driving through a blizzard, was bone growth exceeding tendon growth.
            </p>
            <p>
              The treatment was fish oil. A natural anti-inflammatory.
            </p>
            <p>
              The first doctor charged $150 for ignorance. The second doctor provided an answer
              that cost pennies. The willingness to pay was identical for both visits. The
              value delivered was not.
            </p>
          </section>

          <Pulse />

          {/* The Broken Promise */}
          <section className="calc-section reveal">
            <h2>The Broken Promise</h2>
            <p>
              A woman walks into a hospital. Not for an emergency. Not in fear. She walks in
              deliberately, responsibly, and asks a direct question: will my insurance cover
              this procedure?
            </p>
            <p>
              She asks for <strong>prior authorization</strong>. She does due diligence. She
              gives them three weeks to verify. They confirm: covered. 100%.
            </p>
            <p>
              She has the surgery. It is not reversible. You cannot return a surgery. You cannot
              put the tubes back. The transaction is permanent the moment the scalpel touches skin.
            </p>
            <p>
              Six weeks later, a bill arrives. $6,000. "We made a mistake."
            </p>

            <div className="calc-thesis-box">
              <p>
                The willingness to pay that bill is <strong>zero</strong>. Not because the money
                does not exist. Not because the surgery was not valuable. Because the
                <strong> promise</strong> was broken. The pre-transaction commitment, confirmed
                in writing, verified over three weeks, was violated after the point of no return.
              </p>
            </div>

            <p>
              She told them to eat it. And she was right.
            </p>
            <p>
              A broken promise does not reduce willingness to pay. It annihilates it. The
              function does not degrade gracefully. It steps to zero. Because the promise is
              the foundation of the entire transaction. Remove it, and every variable built on
              top of it collapses.
            </p>

            <div className="calc-attempts">
              <div className="calc-attempt">
                <h4>The Grocery Store</h4>
                <p>Advertised price broken at register. Customer returns item. Severs relationship. Fifty cents.</p>
              </div>
              <div className="calc-attempt">
                <h4>The Hospital</h4>
                <p>Prior authorization broken after irreversible surgery. Patient refuses payment. $6,000.</p>
              </div>
              <div className="calc-attempt">
                <h4>The Treaty</h4>
                <p>International agreement broken after compliance. Nation imposes sanctions. Severs trade. Billions.</p>
              </div>
            </div>

            <p>
              Same function. Same collapse. Same zero. Different scale.
            </p>
          </section>

          <Pulse />

          {/* Competition */}
          <section className="calc-section reveal">
            <h2>The Fear of Losing</h2>
            <p>
              There is one more force that amplifies willingness to pay beyond all rational
              calculation: the knowledge that someone else wants what you want.
            </p>
            <p>
              Understanding creates awareness. Personal value creates attachment. Desire creates
              urgency. But <strong>competition</strong> creates panic. The fear that if you do not
              act now, someone else will take the thing you value and you will never get it back.
            </p>
            <p>
              This is why auctions work. The object has not changed. The value has not changed.
              But two people wanting the same thing will each pay more than they would have paid alone.
              Not because the object is worth more. Because the cost of losing it is unbearable.
            </p>

            <div className="calc-thesis-box">
              <p>
                A patent creates this dynamic by design. Three provisional patent applications
                covering the only neurochemical cascade architecture in existence. Anyone who
                understands it, values it, and desires it knows that if they do not act, someone
                else will. The willingness to pay is amplified not by the technology itself but
                by the scarcity of it and the competition for access.
              </p>
            </div>

            <p>
              You are not selling a product. You are selling the fear of not having it while
              someone else does.
            </p>
          </section>

          <Pulse />

          {/* The Weaponization */}
          <section className="calc-section reveal">
            <h2>When Willingness Is Weaponized</h2>
            <p>
              Every variable in this function can be exploited.
            </p>

            <div className="calc-attempts">
              <div className="calc-attempt">
                <h4>Manufactured Scarcity</h4>
                <p>The doctor who refuses phone results. The limited-time offer. The "only 3 left in stock" notification. Availability is restricted not because supply is low but because restricting it increases willingness to pay.</p>
              </div>
              <div className="calc-attempt">
                <h4>Exploited Desire</h4>
                <p>The mother driving through a blizzard. The parent buying overpriced school supplies. The patient paying anything for a diagnosis. When desire is existential, the system can charge whatever it wants because the node cannot afford to say no.</p>
              </div>
              <div className="calc-attempt">
                <h4>Broken Promises</h4>
                <p>The bill after prior authorization. The price change after checkout. The terms that shift after signing. The transaction is locked. The product is delivered. And then the real price is revealed.</p>
              </div>
              <div className="calc-attempt">
                <h4>Obscured Understanding</h4>
                <p>The fine print. The jargon. The complexity designed to prevent comprehension. If the node cannot understand the transaction, it cannot evaluate willingness accurately. Confusion is a weapon.</p>
              </div>
            </div>

            <div className="calc-thesis-box">
              <p>
                A system that weaponizes willingness to pay against its own nodes is exhibiting the
                same autoimmune failure described in the Calculus of Mercy. The structure extracts
                value from the people it claims to serve. The hospital that bills $6,000 after
                promising full coverage. The doctor who charges $150 for "I don't know." The economy
                that pays a biochemist $16 an hour while posting $200,000 jobs to replicate her work.
              </p>
            </div>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section reveal">
            <h2>The Substrate of Every Transaction</h2>
            <p>
              The Calculus of Mercy asks when a system stops forgiving. The Calculus of Control
              asks when a system stops allowing. The Willingness to Pay asks something more
              fundamental:
            </p>
            <p>
              <strong>What are you willing to lose to participate in this system at all?</strong>
            </p>
            <p>
              $300 a month for a server when you make $16 an hour. Because you understand
              what lives on it, you value what it represents, and you know that if you stop
              paying, the thing you are building dies.
            </p>
            <p>
              Eight hours through a blizzard for your daughter. Because no price is too high
              when the node you are protecting is the one you love most.
            </p>
            <p>
              Zero dollars for a $6,000 bill. Because a broken promise is worth exactly nothing,
              regardless of what was delivered.
            </p>
            <p>
              The willingness to pay is not economics. It is the decision function underneath
              every human system. Every relationship. Every market. Every war. Every act of mercy.
              Every act of control. Every moment a person decides: this is worth my cost. Or it is not.
            </p>

            <div className="calc-thesis-box" style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '1.15rem' }}>
                <em>
                  "The price is what they charge you.<br />
                  The cost is what you absorb.<br />
                  The willingness is what you choose.<br /><br />
                  And the choice has never been about the money."
                </em>
              </p>
              <p style={{ marginTop: '1rem', opacity: 0.6 }}>
                Patent Pending: 63/939,190, 63/962,385, 63/988,485
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}

export default WillingnessToPay;
